import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PublicNav from '@/components/PublicNav';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import ZEmptyState from '@/components/shared/ZEmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const S = {
  bg: '#0a0a0f',
  panel: 'rgba(255,255,255,0.02)',
  cream: '#E8E3D9',
  secondary: '#b0b4c0',
  border: 'rgba(255,255,255,0.06)',
  gold: '#C9A84C',
  goldDim: 'rgba(201,168,76,0.25)',
  serif: "'Cormorant Garamond', serif",
  mono: "'DM Mono', monospace",
};

function TagPill({ tag }) {
  return (
    <span style={{
      fontFamily: S.mono, fontSize: 10, color: S.gold,
      border: `1px solid ${S.goldDim}`, padding: '2px 8px', letterSpacing: '0.1em',
      textTransform: 'uppercase',
    }}>{tag}</span>
  );
}

function Entry({ entry }) {
  return (
    <div style={{ padding: '24px 0', borderBottom: `1px solid ${S.border}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <p style={{ fontFamily: S.serif, fontSize: 26, color: S.cream, lineHeight: 1.2, margin: 0 }}>{entry.name}</p>
        {entry.url && (
          <a href={entry.url} target="_blank" rel="noopener noreferrer" style={{ color: S.goldDim, marginTop: 6, flexShrink: 0, transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = S.gold}
            onMouseLeave={e => e.currentTarget.style.color = S.goldDim}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        )}
      </div>
      {entry.image_url && (
        <div style={{ marginTop: 14 }}>
          <img
            src={entry.image_url}
            alt=""
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              maxHeight: 460,
              objectFit: 'contain',
              background: '#0a0a0f',
            }}
          />
        </div>
      )}
      {entry.note && (
        <p style={{ fontFamily: S.serif, fontSize: 16, fontStyle: 'italic', color: S.secondary, lineHeight: 1.75, marginTop: entry.image_url ? 14 : 8 }}>{entry.note}</p>
      )}
      {entry.tags && entry.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {entry.tags.map(t => <TagPill key={t} tag={t} />)}
        </div>
      )}
    </div>
  );
}

function Section({ section }) {
  const hasPhoto = !!section.photo_url;
  const entries = [...(section.entries || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div style={{
      background: S.panel,
      border: `1px solid ${S.border}`,
      borderTop: `2px solid ${S.gold}`,
      marginBottom: 24,
      overflow: 'hidden',
    }}>
      {hasPhoto && (
        // Natural aspect ratio, no fixed height, no gradient overlay.
        // Previously used object-fit: cover at 300px which cropped portraits
        // and wide landscapes. Now the full photo is visible; max-height caps
        // a super-tall portrait from dominating the page.
        <img
          src={section.photo_url}
          alt=""
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            maxHeight: 520,
            objectFit: 'contain',
            background: '#0a0a0f',
          }}
        />
      )}
      {/* Section header */}
      <div style={{ padding: '20px 28px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: S.mono, fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: S.gold }}>{section.title}</span>
        <div style={{ flex: 1, height: 1, background: S.goldDim }} />
      </div>
      {/* Entries */}
      <div style={{ padding: '0 28px 8px' }}>
        {entries.map(entry => <Entry key={entry.id} entry={entry} />)}
      </div>
    </div>
  );
}

export default function CapeTownPublic() {
  const { slug } = useParams();
  const guideSlug = slug || 'capetown';
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=DM+Mono:wght@300;400&display=swap';
    document.head.appendChild(link);

    axios.get(`${API}/curated/${guideSlug}`)
      .then(r => {
        setGuide(r.data);
        document.title = `${r.data.title} · Werner du Toit`;
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return () => { document.head.removeChild(link); };
  }, [guideSlug]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: S.bg }}>
        <ZLoadingBar accent="#F5A623" loading />
      </div>
    );
  }

  if (!guide) {
    return (
      <div style={{ minHeight: '100vh', background: S.bg }}>
        <ZEmptyState mark="C" accent="#F5A623" message="Not found." />
      </div>
    );
  }

  const sections = [...(guide.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div style={{ minHeight: '100vh', background: S.bg, color: S.cream }}>
      <PublicNav />
      {/* Hero — natural aspect ratio so the whole photo is visible, with a
           max-height cap to keep the title block on-screen on short viewports.
           Title overlays the bottom of the photo via negative margin. */}
      {guide.hero_image_url ? (
        <div style={{ position: 'relative', background: '#0a0a0f' }}>
          <img
            src={guide.hero_image_url}
            alt=""
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              maxHeight: '75vh',
              objectFit: 'contain',
              background: '#0a0a0f',
            }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(13,13,13,0) 50%, rgba(13,13,13,0.85) 100%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: 40, left: 0, right: 0, padding: '0 40px', maxWidth: 760 }}>
            <h1 style={{ fontFamily: S.serif, fontSize: 'clamp(40px, 8vw, 72px)', fontWeight: 300, color: S.cream, margin: '0 0 16px', lineHeight: 1.05 }}>{guide.title}</h1>
            <p style={{ fontFamily: S.mono, fontSize: 13, color: 'rgba(232,227,217,0.65)', letterSpacing: '0.12em' }}>{guide.subtitle}</p>
          </div>
        </div>
      ) : (
        <div style={{ position: 'relative', height: 360, background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, #0a0a0f 100%)' }}>
          <div style={{ position: 'absolute', bottom: 40, left: 0, right: 0, padding: '0 40px', maxWidth: 760 }}>
            <h1 style={{ fontFamily: S.serif, fontSize: 'clamp(40px, 8vw, 72px)', fontWeight: 300, color: S.cream, margin: '0 0 16px', lineHeight: 1.05 }}>{guide.title}</h1>
            <p style={{ fontFamily: S.mono, fontSize: 13, color: 'rgba(232,227,217,0.65)', letterSpacing: '0.12em' }}>{guide.subtitle}</p>
          </div>
        </div>
      )}

      {/* Credit bar */}
      <div style={{ padding: '16px 40px', borderBottom: `1px solid ${S.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: S.mono, fontSize: 10, color: S.secondary, letterSpacing: '0.18em', textTransform: 'uppercase', margin: 0 }}>A Zephyr OS production · wernerdutoit.co.za</p>
      </div>

      {/* Intro */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '64px 40px 0' }}>
        <p style={{ fontFamily: S.serif, fontSize: 20, lineHeight: 1.8, color: S.cream, fontStyle: 'italic' }}>{guide.intro}</p>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 0' }}>
        {sections.map((section) => (
          <Section key={section.id} section={section} />
        ))}
      </div>

      {/* Footer */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px', borderTop: `1px solid ${S.border}`, marginTop: 48 }}>
        <p style={{ fontFamily: S.mono, fontSize: 11, color: S.secondary, letterSpacing: '0.15em', textTransform: 'uppercase', textAlign: 'center' }}>
          Curated by Werner du Toit · Cape Town · 2026 Edition · A Zephyr OS production
        </p>
      </div>
    </div>
  );
}
